// services/school-ui/src/pages/promotion/PromotionDetail.tsx
// Right pane: header + preview body + decision footer. Pure
// composition — no data fetching of its own. Receives the selected
// PromotionRequest from the page; passes the decision callback up
// for auto-advance + undo banner orchestration.

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useEligibleTeachers } from "../../api/school";
import { DecisionFooter } from "./DecisionFooter";
import { PreviewPane } from "./PreviewPane";
import { renderRequester } from "./QueueList";
import type { PromotionRequest } from "./types";

interface Props {
  request: PromotionRequest | null;
  onDecided: (action: "approve" | "reject", notes: string) => void;
  onError: (message: string) => void;
}

export function PromotionDetail({ request, onDecided, onError }: Props): JSX.Element {
  const { t } = useTranslation();
  const teachers = useEligibleTeachers();
  const userNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const tt of teachers.data ?? []) {
      m.set(tt.user_id, tt.display_name || tt.email);
    }
    return m;
  }, [teachers.data]);
  if (!request) {
    return (
      <div className="flex h-full flex-1 items-center justify-center text-sm italic text-slate-500">
        {t("promotion.queue.selectPrompt")}
      </div>
    );
  }
  return (
    <div className="flex h-full flex-1 flex-col">
      <header className="border-b border-slate-200 px-6 py-4">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs">
          <span className="text-slate-700 font-medium">
            {renderRequester(request.requested_by, userNameById)}
          </span>
          <span className="text-slate-500">·</span>
          <code className="text-blue-600">{request.source_collection}</code>
          <span className="text-slate-500">→</span>
          <code className="text-blue-600">{request.target_collection}</code>
          <span className="ml-auto text-slate-500">
            {request.requested_at.slice(0, 16).replace("T", " ")}
          </span>
        </div>
      </header>
      <PreviewPane requestId={request.id} />
      <DecisionFooter request={request} onDecided={onDecided} onError={onError} />
    </div>
  );
}
